import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaPaperclip, FaTrash, FaDownload, FaPaperPlane, FaEdit } from "react-icons/fa";

export default function ExpenditureTab({ garages = [] }) {
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const userId = sessionStorage.getItem("user_id");

    const [selectedGarage, setSelectedGarage] = useState("");
    const [garageSearch, setGarageSearch] = useState("");
    const [garageDropdownOpen, setGarageDropdownOpen] = useState(false);
    const [detectedGarage, setDetectedGarage] = useState(null);
    const [messages, setMessages] = useState([]);
    const [expenditures, setExpenditures] = useState([]);
    const [generalMessages, setGeneralMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState("");
    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("expenditures");
    const [detectionError, setDetectionError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ item_name: "", quantity: 1, amount_spent: "", purchase_date: "" });
    const fileInputRef = useRef(null);
    const garageDropdownRef = useRef(null);

    const filteredGarages = garages.filter((garage) =>
        garage.GarageName?.toLowerCase().includes(garageSearch.toLowerCase().trim())
    );

    const selectedGarageOption = selectedGarage
        ? garages.find(
            (garage) =>
                String(garage.GarageID) === String(selectedGarage)
        )
        : null;

    // Determine active garage: selectedGarage takes priority, then detectedGarage
    const activeGarage = selectedGarage
        ? garages.find(g => String(g.GarageID) === String(selectedGarage))
        : detectedGarage;

    // Load expenditures and messages when garage changes
    useEffect(() => {
        if (activeGarage) {
            loadExpenditures();
            loadGeneralMessages();
        }
    }, [activeGarage]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (garageDropdownRef.current && !garageDropdownRef.current.contains(event.target)) {
                setGarageDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fuzzy match garage name
    const findGarageByName = (garageName) => {
        if (!garageName || garages.length === 0) return null;
        
        const searchTerm = garageName.toLowerCase().trim();
        
        // Exact match first
        let match = garages.find(g => g.GarageName.toLowerCase() === searchTerm);
        if (match) return match;
        
        // Partial match (contains)
        match = garages.find(g => g.GarageName.toLowerCase().includes(searchTerm));
        if (match) return match;
        
        // Reverse partial match (search term contains garage name)
        match = garages.find(g => searchTerm.includes(g.GarageName.toLowerCase()));
        if (match) return match;
        
        // Fuzzy match - check if most characters match
        const bestMatch = garages.reduce((best, garage) => {
            const gName = garage.GarageName.toLowerCase();
            let matches = 0;
            for (let char of searchTerm) {
                if (gName.includes(char)) matches++;
            }
            const score = matches / Math.max(searchTerm.length, gName.length);
            return score > (best.score || 0) ? { garage, score } : best;
        }, {});
        
        if (bestMatch.score && bestMatch.score > 0.5) {
            return bestMatch.garage;
        }
        
        return null;
    };

    const loadExpenditures = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}/expenditure/expenditures/${activeGarage.GarageID}?user_id=${userId}`
            );
            setExpenditures(response.data);
        } catch (error) {
            console.error("Error loading expenditures:", error);
        }
    };

    const loadGeneralMessages = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}/expenditure/messages/${activeGarage.GarageID}?user_id=${userId}`
            );
            setGeneralMessages(response.data);
        } catch (error) {
            console.error("Error loading messages:", error);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
            if (!allowedTypes.includes(file.type)) {
                alert("Only PDF, JPG, and PNG files are allowed");
                return;
            }
            setReceipt(file);
        }
    };

    // Extract garage name from message using regex patterns
    const extractGarageName = (text) => {
        const textLower = text.toLowerCase();
        
        // Patterns to extract facility/garage name
        const facilityPatterns = [
            /(?:at|in|for|garage|facility|parking|location)\s+(?:the\s+)?([^,\.!?]+?)(?:\s+(?:on|cost|amount|total|paid|spent)|\s*$)/i,
            /(?:downtown|midtown|central|north|south|east|west)\s+([^,\.!?]+?)(?:\s+(?:garage|facility|parking))?/i,
        ];
        
        for (let pattern of facilityPatterns) {
            const match = text.match(pattern);
            if (match) {
                let facility = match[1].trim();
                // Clean up
                facility = facility.replace(/\s+/g, ' ');
                if (facility && facility.length > 2) {
                    return facility;
                }
            }
        }
        
        return null;
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) {
            alert("Please enter a message");
            return;
        }

        // Determine which garage to use
        let garageToUse = null;

        if (selectedGarage) {
            // If user selected a garage from dropdown, use that (highest priority)
            garageToUse = garages.find(g => g.GarageID === selectedGarage);
        } else {
            // Otherwise, try to extract garage name from message
            const extractedGarageName = extractGarageName(inputMessage);
            if (!extractedGarageName) {
                setDetectionError("Please either select a garage from the dropdown or mention the garage name in your message (e.g., 'at Downtown Garage')");
                return;
            }

            // Find matching garage
            const matchedGarage = findGarageByName(extractedGarageName);
            if (!matchedGarage) {
                setDetectionError(`Could not find a matching garage for "${extractedGarageName}". Available garages: ${garages.map(g => g.GarageName).join(", ")}`);
                return;
            }

            garageToUse = matchedGarage;
            setDetectedGarage(matchedGarage);
        }

        setDetectionError("");

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("garage_id", garageToUse.GarageID);
            formData.append("message", inputMessage);
            formData.append("user_id", userId);
            if (receipt) {
                formData.append("receipt", receipt);
            }

            const response = await axios.post(
                `${BASE_URL}/expenditure/submit-chat-message`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // Add message to chat
            const newMessage = {
                type: response.data.type,
                timestamp: new Date().toLocaleString(),
                content: inputMessage,
                data: response.data.data,
            };
            setMessages([...messages, newMessage]);

            // Refresh data
            loadExpenditures();
            loadGeneralMessages();

            // Clear input
            setInputMessage("");
            setReceipt(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (error) {
            console.error("Error sending message:", error);
            let errorMsg = "An error occurred";
            if (error.response?.data?.detail) {
                errorMsg = error.response.data.detail;
            } else if (error.response?.data) {
                errorMsg = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data);
            } else if (error.message) {
                errorMsg = error.message;
            }
            alert("Error: " + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReceipt = async (expenditureId, filename) => {
        try {
            const response = await axios.get(
                `${BASE_URL}/expenditure/receipt/${expenditureId}?user_id=${userId}`,
                {
                    responseType: "blob",
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error downloading receipt:", error);
            alert("Error downloading receipt");
        }
    };

    const handleEditClick = (expenditure) => {
        setEditingId(expenditure.id);
        setEditForm({
            item_name: expenditure.item_name,
            quantity: expenditure.quantity || 1,
            amount_spent: expenditure.amount_spent,
            purchase_date: new Date(expenditure.purchase_date).toISOString().split('T')[0]
        });
    };

    const handleSaveEdit = async () => {
        if (!editForm.item_name.trim() || !editForm.quantity || !editForm.amount_spent || !editForm.purchase_date) {
            alert("Please fill in all fields");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("item_name", editForm.item_name);
            formData.append("quantity", parseInt(editForm.quantity));
            formData.append("amount_spent", parseFloat(editForm.amount_spent));
            formData.append("purchase_date", editForm.purchase_date);
            formData.append("user_id", userId);

            await axios.put(
                `${BASE_URL}/expenditure/expenditure/${editingId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setEditingId(null);
            loadExpenditures();
            alert("Expenditure updated successfully");
        } catch (error) {
            console.error("Error updating expenditure:", error);
            alert("Error: " + (error.response?.data?.detail || error.message));
        }
    };

    const handleDeleteClick = async (expenditureId) => {
        if (!window.confirm("Are you sure you want to delete this expenditure?")) {
            return;
        }

        try {
            await axios.delete(
                `${BASE_URL}/expenditure/expenditure/${expenditureId}?user_id=${userId}`
            );

            loadExpenditures();
            alert("Expenditure deleted successfully");
        } catch (error) {
            console.error("Error deleting expenditure:", error);
            alert("Error: " + (error.response?.data?.detail || error.message));
        }
    };

    return (
        <div className="flex flex-col h-full gap-4">
            {/* Garage Selector - Optional */}
            <div className="bg-white p-4 rounded-lg shadow">
                <label className="block text-sm font-medium mb-2">Select Garage</label>
                <div ref={garageDropdownRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setGarageDropdownOpen((prev) => !prev)}
                        className="w-full text-left border rounded px-3 py-2 flex items-center justify-between"
                    >
                        <span>
                            {selectedGarageOption ? selectedGarageOption.GarageName : "-- Select garage --"}
                        </span>
                        <span className="text-gray-500">{garageDropdownOpen ? "▲" : "▼"}</span>
                    </button>

                    {garageDropdownOpen && (
                        <div className="absolute z-10 mt-2 w-full bg-white border rounded shadow max-h-72 overflow-hidden">
                            <div className="p-2 border-b">
                                <input
                                    type="text"
                                    value={garageSearch}
                                    onChange={(e) => setGarageSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Search garages..."
                                    className="w-full border rounded px-3 py-2 focus:outline-none"
                                />
                            </div>
                            <div className="max-h-56 overflow-auto">
                                {filteredGarages.length > 0 ? (
                                    filteredGarages.map((garage) => (
                                        <button
                                            key={garage.GarageID}
                                            type="button"
                                            onClick={() => {
                                                setSelectedGarage(String(garage.GarageID));
                                                setGarageDropdownOpen(false);
                                            }}
                                            className="w-full text-left px-3 py-2 hover:bg-blue-50"
                                        >
                                            {garage.GarageName}
                                        </button>
                                    ))
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        No matching garages found
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {selectedGarage && (
                    <p className="text-xs text-green-600 mt-2">
                        ✓ Garage selected: {garages.find(g => String(g.GarageID) === String(selectedGarage))?.GarageName}
                    </p>
                )}
            </div>

            {/* Garage Detection Info */}
            {!selectedGarage && detectedGarage && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <p className="text-sm font-medium text-green-800">
                        ✓ Detected Garage: <span className="font-bold">{detectedGarage.GarageName}</span>
                    </p>
                </div>
            )}

            {detectionError && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <p className="text-sm text-red-800">{detectionError}</p>
                </div>
            )}

            {/* {!selectedGarage && !detectedGarage && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                        💡 Select a garage from the dropdown above, or mention a garage name in your message.
                    </p>
                </div>
            )} */}

            {activeGarage && (
                <>
                    {/* Tabs */}
                    <div className="flex gap-2 border-b">
                        <button
                            onClick={() => setActiveTab("expenditures")}
                            className={`px-4 py-2 font-medium ${
                                activeTab === "expenditures"
                                    ? "border-b-2 border-blue-600 text-blue-600"
                                    : "text-gray-600"
                            }`}
                        >
                            Expenditures ({expenditures.length})
                        </button>
                        <button
                            onClick={() => setActiveTab("messages")}
                            className={`px-4 py-2 font-medium ${
                                activeTab === "messages"
                                    ? "border-b-2 border-blue-600 text-blue-600"
                                    : "text-gray-600"
                            }`}
                        >
                            Messages ({generalMessages.length})
                        </button>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 bg-white rounded-lg shadow overflow-y-auto p-4">
                        {activeTab === "expenditures" && (
                            <div>
                                {expenditures.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">
                                        No expenditures logged yet
                                    </p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="bg-gray-100 border-b">
                                                    <th className="px-4 py-2 text-left text-sm font-semibold">Item</th>
                                                    <th className="px-4 py-2 text-center text-sm font-semibold">Qty</th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold">Amount</th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold">Receipt</th>
                                                    <th className="px-4 py-2 text-center text-sm font-semibold">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {expenditures.map((exp) => (
                                                    <tr key={exp.id} className="border-b hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm">{exp.item_name}</td>
                                                        <td className="px-4 py-3 text-sm text-center">{exp.quantity || 1}</td>
                                                        <td className="px-4 py-3 text-sm font-semibold text-blue-600">
                                                            ${parseFloat(exp.amount_spent).toFixed(2)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            {new Date(exp.purchase_date).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            {exp.receipt_filename ? (
                                                                <button
                                                                    onClick={() =>
                                                                        handleDownloadReceipt(
                                                                            exp.id,
                                                                            exp.receipt_filename
                                                                        )
                                                                    }
                                                                    className="text-blue-500 hover:text-blue-700 underline"
                                                                >
                                                                    Download
                                                                </button>
                                                            ) : (
                                                                <span className="text-gray-400">-</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <div className="flex gap-2 justify-center">
                                                                <button
                                                                    onClick={() => handleEditClick(exp)}
                                                                    className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-xs"
                                                                >
                                                                    <FaEdit /> Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteClick(exp.id)}
                                                                    className="flex items-center gap-1 bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-xs"
                                                                >
                                                                    <FaTrash /> Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "messages" && (
                            <div className="space-y-3">
                                {generalMessages.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">
                                        No messages logged yet
                                    </p>
                                ) : (
                                    generalMessages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className="border rounded-lg p-4 bg-gray-50"
                                        >
                                            <p className="text-gray-800">{msg.message_text}</p>
                                            <p className="text-xs text-gray-500 mt-2">
                                                {new Date(msg.created_at).toLocaleString()}
                                            </p>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Chat Input - Always Visible */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        placeholder={selectedGarage ? "Type expenditure or message...(e.g., 'Bought ELKA Parking gate for $2800')" : "Type expenditure or message... (e.g., 'Spent $50 on paint at Downtown Garage on 2025-03-20')"}
                        className="flex-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center gap-2"
                    >
                        <FaPaperclip /> Attach
                    </button>
                    <button
                        onClick={handleSendMessage}
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
                    >
                        <FaPaperPlane /> Send
                    </button>
                </div>

                {receipt && (
                    <div className="flex items-center gap-2 text-sm text-green-600 mb-2">
                        <FaPaperclip /> {receipt.name}
                        <button
                            onClick={() => {
                                setReceipt(null);
                                if (fileInputRef.current) {
                                    fileInputRef.current.value = "";
                                }
                            }}
                            className="text-red-500 hover:text-red-700"
                        >
                            <FaTrash />
                        </button>
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                />

                <p className="text-xs text-gray-500">
                    Tip: Select a garage from the dropdown to log multiple expenses.
                </p>
            </div>

            {/* Edit Modal */}
            {editingId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                        <h2 className="text-xl font-semibold mb-4">Edit Expenditure</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Item Name</label>
                                <input
                                    type="text"
                                    value={editForm.item_name}
                                    onChange={(e) => setEditForm({ ...editForm, item_name: e.target.value })}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Quantity</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={editForm.quantity}
                                    onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Amount</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={editForm.amount_spent}
                                    onChange={(e) => setEditForm({ ...editForm, amount_spent: e.target.value })}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Date</label>
                                <input
                                    type="date"
                                    value={editForm.purchase_date}
                                    onChange={(e) => setEditForm({ ...editForm, purchase_date: e.target.value })}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleSaveEdit}
                                className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setEditingId(null)}
                                className="flex-1 bg-gray-400 text-white py-2 rounded hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
